// ── Never send a token the server will reject ────────────────────────
//
// `supabase.auth.getSession()` returns the CACHED session. When a tab has been
// in the background the browser throttles supabase-js's refresh timer, so the
// cached token can be minutes past its expiry — and the caller puts it straight
// into an Authorization header. The auth log for 2026-08-22 → 23 carried 807 of
// those: `GET /user → 403 token has invalid claims: token is expired`, referer
// app.fitopspro.com, roughly 550ms each, every one of them a request that was
// never going to succeed.
//
// ⚠️ THIS WRAPS THE CLIENT, NOT THE CALLERS, AND THAT IS THE WHOLE POINT. The
// first attempt (2026-08-22) put the same check inside modules/api.js. The 403s
// continued because 208 other call sites build
// `Authorization: Bearer ${session.access_token}` by hand from their own
// getSession() and never go near api.js. One wrapper on the single shared
// client covers all 209.

/**
 * Is this cached session too close to expiry to put on the wire?
 *
 * @param {{expires_at?: number|string}|null|undefined} session
 * @param {number} nowMs
 * @param {number} slackMs headroom for clock skew and time in flight
 * @returns {boolean} true when it should be refreshed first
 */
export function needsRefresh(session, nowMs, slackMs) {
  // ⚠️ `Number(null)` IS 0, NOT NaN. Reading expires_at off a missing session
  // through `&&` yields null, which scores as "expired in 1970" and made a
  // signed-out client try to refresh on every call. Check the session first.
  if (!session) return false;
  const expSec = Number(session.expires_at);
  // ⚠️ UNKNOWN IS NOT EXPIRED. An older stored shape has no expires_at, and
  // treating that as stale would refresh on every single call.
  if (!Number.isFinite(expSec)) return false;
  return (expSec * 1000) - nowMs < slackMs;
}

/**
 * Wrap `client.auth.getSession` so it refreshes a stale session before
 * returning it. Idempotent — installing twice wraps once.
 *
 * @param {any} client a supabase-js client
 * @param {{slackMs?: number, now?: () => number}} [opts]
 * @returns {any} the same client
 */
export function installFreshSession(client, opts = {}) {
  const auth = client && client.auth;
  if (!auth || auth.__freshSessionInstalled) return client;

  const slackMs = opts.slackMs ?? 30_000;
  const now = opts.now || (() => Date.now());
  const original = auth.getSession.bind(auth);

  // ⚠️ RE-ENTRANCY GUARD, NOT AN OPTIMISATION. supabase-js's own
  // refreshSession() calls getSession() to find the refresh token, so without
  // this the wrapper calls itself forever and takes the dashboard down — a far
  // worse failure than the 403 it replaces.
  let refreshing = false;

  auth.getSession = async function freshGetSession(...args) {
    const res = await original(...args);
    const session = res && res.data && res.data.session;
    if (refreshing || !needsRefresh(session, now(), slackMs)) return res;

    refreshing = true;
    try {
      // The refresh token is passed explicitly so this never depends on the
      // very call we are inside. supabase-js coalesces concurrent refreshes,
      // so a burst of parallel callers shares one round trip.
      const out = await auth.refreshSession({ refresh_token: session.refresh_token });
      const next = out && out.data && out.data.session;
      if (next) return { ...res, data: { ...res.data, session: next } };
    } catch (e) {
      // Fall through with the stale session on purpose: the 401 path is the
      // backstop, and a refresh that failed here fails there too. One clean
      // error beats a screen that renders nothing.
      console.warn('[auth] pre-flight refresh failed:', e && e.message);
    } finally {
      refreshing = false;
    }
    return res;
  };

  auth.__freshSessionInstalled = true;
  return client;
}
